import { EmailInput, PasswordInput } from '@frontend/components/basic-forms';
import { DefaultPrimaryButton } from '@frontend/components/common';
import { ErrorHandler } from '@frontend/error-handler';
import { ICONS } from '@frontend/icons';
import { IdentificationBasicAuthClient, IdentificationClient, MyIdentificationClient } from '@frontend/identification/api';
import { ChangeBasicAuthPassword, Identification } from '@frontend/identification/types';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { ToastUtil } from '@frontend/toast-utils';
import type { MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

export interface BasicAuthUpdateProps {
    basic_auth: Identification;
    user_id: string;
    updateMode?: 'patch' | 'change-password';
    selfService?: boolean;
    onSubmitSuccess?: (value: Identification) => void;
}

export const BasicAuthUpdate = (props: BasicAuthUpdateProps) => {
    if (props.selfService || props.updateMode !== 'change-password') {
        return <BasicAuthPatchUpdate {...props} />;
    }

    return <BasicAuthChangePasswordUpdate {...props} />;
};

const BasicAuthPatchUpdate = (props: BasicAuthUpdateProps) => <BasicAuthPatchUpdateForm {...props} />;

const BasicAuthPatchUpdateForm = (props: BasicAuthUpdateProps) => {
    const { closeTopModal } = useModals();
    const [formValues, setFormValues] = useState<{ key: string; old_password: string; secret: string | null; confirm_secret: string }>({
        key: props.basic_auth.key,
        old_password: '',
        secret: null,
        confirm_secret: ''
    });
    const [emailValid, setEmailValid] = useState<boolean>(true);
    const [passwordValid, setPasswordValid] = useState<boolean>(true);
    const [confirmPasswordValid, setConfirmPasswordValid] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const isChangingPassword = !!formValues.secret;
    const requiresOldPassword = !!props.selfService && isChangingPassword;
    const canEditNewPassword = !props.selfService || !!formValues.old_password;
    const passwordsMatch = !isChangingPassword || formValues.secret === formValues.confirm_secret;

    const submitDisabled = useMemo(
        () =>
            isSubmitting ||
            !emailValid ||
            !passwordValid ||
            !confirmPasswordValid ||
            !formValues.key ||
            (requiresOldPassword && !formValues.old_password) ||
            (isChangingPassword && (!formValues.confirm_secret || !passwordsMatch)),
        [
            confirmPasswordValid,
            emailValid,
            formValues.confirm_secret,
            formValues.key,
            formValues.old_password,
            isChangingPassword,
            isSubmitting,
            passwordValid,
            passwordsMatch,
            requiresOldPassword
        ]
    );

    const handleSubmit = async (e?: MouseEvent<HTMLButtonElement>) => {
        e?.preventDefault();
        if (submitDisabled) return;

        setIsSubmitting(true);
        try {
            const identification =
                props.selfService && isChangingPassword
                    ? await IdentificationBasicAuthClient.changePassword(props.basic_auth.account_id, props.user_id, {
                          email: formValues.key,
                          old_password: formValues.old_password,
                          new_password: formValues.secret ?? ''
                      })
                    : await (props.selfService
                          ? MyIdentificationClient.patchIdentification(props.basic_auth.account_id, props.basic_auth.id, {
                                key: formValues.key,
                                secret: formValues.secret
                            })
                          : IdentificationClient.patchIdentification(props.basic_auth.account_id, props.basic_auth.id, {
                                key: formValues.key,
                                secret: formValues.secret
                            }));
            ToastUtil.success(
                CommonMessage.TOAST.PROMISE.SUCCESS,
                <FormattedMessage
                    id='basic_auth.update.success'
                    defaultMessage='Basic authentication updated successfully'
                />
            );
            props.onSubmitSuccess?.(identification);
            closeTopModal();
        } catch (e: any) {
            ErrorHandler.handleError({ error: e, entity_type: CommonMessage.OBJECTS.IDENTIFICATION.DEFAULT });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form
            className='modal bg-background-default flex h-full flex-col gap-6 rounded-xl p-6'
            onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit();
            }}>
            <div className='modal-header px-0 py-0'>
                <div className='inline-flex w-full items-center justify-between self-stretch'>
                    <div className='text-foreground-default justify-start text-3xl font-semibold'>
                        <FormattedMessage
                            id='basic_auth.update.title'
                            defaultMessage='Update basic authentication'
                        />
                    </div>
                    <button
                        type='button'
                        onClick={closeTopModal}
                        className='bg-background-surface hover:bg-brand-light border-border-default flex size-11 cursor-pointer items-center justify-center self-center rounded-md border'>
                        <ICONS.BUTTON.CLOSE_2 className='text-brand-muted size-6' />
                    </button>
                </div>
            </div>

            <div className='bg-background-surface border-border-default inline-flex flex-col items-start justify-start gap-3 self-stretch rounded-xl border p-4'>
                <EmailInput
                    id='basic-auth-email'
                    required
                    label={CommonMessage.FORMS.EMAIL()}
                    value={formValues.key}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, key: value }))}
                    isValidCallback={setEmailValid}
                />
                {props.selfService && (
                    <PasswordInput
                        id='basic-auth-old-password'
                        label={
                            <FormattedMessage
                                id='basic-auth-update.old-password'
                                description='Label for old password when a user changes their own basic auth password'
                                defaultMessage='Old password'
                            />
                        }
                        required={isChangingPassword}
                        noAutoComplete
                        value={formValues.old_password}
                        onChange={(value) =>
                            setFormValues((prev) => ({
                                ...prev,
                                old_password: value,
                                ...(value ? {} : { secret: null, confirm_secret: '' })
                            }))
                        }
                    />
                )}
                <div className={`w-full ${!canEditNewPassword ? 'pointer-events-none opacity-50' : ''}`}>
                    <PasswordInput
                        id='basic-auth-password'
                        label={
                            props.selfService ? (
                                <FormattedMessage
                                    id='basic-auth-update.new-password'
                                    description='Label for new password when a user changes their own basic auth password'
                                    defaultMessage='New password'
                                />
                            ) : (
                                CommonMessage.FORMS.PASSWORD
                            )
                        }
                        strength
                        noAutoComplete
                        required={isChangingPassword}
                        value={formValues.secret ?? ''}
                        onChange={(value) =>
                            setFormValues((prev) => ({
                                ...prev,
                                secret: value || null,
                                confirm_secret: value ? prev.confirm_secret : ''
                            }))
                        }
                        isValidCallback={setPasswordValid}
                    />
                </div>
                <div className={`w-full ${!canEditNewPassword || !isChangingPassword ? 'pointer-events-none opacity-50' : ''}`}>
                    <PasswordInput
                        id='basic-auth-confirm-password'
                        label={
                            <FormattedMessage
                                id='basic-auth-update.confirm-password'
                                description='Label for confirming the new basic auth password'
                                defaultMessage='Repeat password'
                            />
                        }
                        required={isChangingPassword}
                        noAutoComplete
                        value={formValues.confirm_secret}
                        onChange={(value) => setFormValues((prev) => ({ ...prev, confirm_secret: value }))}
                        isValidCallback={setConfirmPasswordValid}
                    />
                </div>
                {isChangingPassword && !passwordsMatch && (
                    <div className='text-error text-sm'>
                        <FormattedMessage
                            id='basic-auth-update.passwords-must-match'
                            description='Validation message when repeated basic auth password does not match'
                            defaultMessage='Passwords must match.'
                        />
                    </div>
                )}
            </div>

            <div className='flex w-full items-start justify-end gap-3'>
                <DefaultPrimaryButton
                    text={CommonMessage.BUTTONS.SUBMIT}
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    isLoading={isSubmitting}
                />
            </div>
        </form>
    );
};

const BasicAuthChangePasswordUpdate = (props: BasicAuthUpdateProps) => {
    const { closeTopModal } = useModals();
    const [formValues, setFormValues] = useState<ChangeBasicAuthPassword & { confirm_new_password: string }>({
        email: props.basic_auth.key,
        old_password: '',
        new_password: '',
        confirm_new_password: ''
    });
    const [emailValid, setEmailValid] = useState<boolean>(true);
    const [oldPasswordValid, setOldPasswordValid] = useState<boolean>(false);
    const [newPasswordValid, setNewPasswordValid] = useState<boolean>(false);
    const [confirmNewPasswordValid, setConfirmNewPasswordValid] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const passwordsMatch = formValues.new_password === formValues.confirm_new_password;

    const submitDisabled = useMemo(
        () =>
            isSubmitting ||
            !emailValid ||
            !oldPasswordValid ||
            !newPasswordValid ||
            !confirmNewPasswordValid ||
            !formValues.email ||
            !formValues.old_password ||
            !formValues.new_password ||
            !formValues.confirm_new_password ||
            !passwordsMatch,
        [
            confirmNewPasswordValid,
            emailValid,
            formValues.confirm_new_password,
            formValues.email,
            formValues.new_password,
            formValues.old_password,
            isSubmitting,
            newPasswordValid,
            oldPasswordValid,
            passwordsMatch
        ]
    );

    const handleSubmit = async (e?: MouseEvent<HTMLButtonElement>) => {
        e?.preventDefault();
        if (submitDisabled) return;

        setIsSubmitting(true);
        try {
            const identification = await IdentificationBasicAuthClient.changePassword(props.basic_auth.account_id, props.user_id, {
                email: formValues.email,
                old_password: formValues.old_password,
                new_password: formValues.new_password
            });
            ToastUtil.success(
                CommonMessage.TOAST.PROMISE.SUCCESS,
                <FormattedMessage
                    id='basic_auth.update.success'
                    defaultMessage='Basic authentication updated successfully'
                />
            );
            props.onSubmitSuccess?.(identification);
            closeTopModal();
        } catch (e: any) {
            ErrorHandler.handleError({ error: e, entity_type: CommonMessage.OBJECTS.IDENTIFICATION.DEFAULT });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form
            className='modal bg-background-default flex h-full flex-col gap-6 rounded-xl p-6'
            onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit();
            }}>
            <div className='modal-header px-0 py-0'>
                <div className='inline-flex w-full items-center justify-between self-stretch'>
                    <div className='text-foreground-default justify-start text-3xl font-semibold'>
                        <FormattedMessage
                            id='basic_auth.update.title'
                            defaultMessage='Update basic authentication'
                        />
                    </div>
                    <button
                        type='button'
                        onClick={closeTopModal}
                        className='bg-background-surface hover:bg-brand-light border-border-default flex size-11 cursor-pointer items-center justify-center self-center rounded-md border'>
                        <ICONS.BUTTON.CLOSE_2 className='text-brand-muted size-6' />
                    </button>
                </div>
            </div>

            <div className='bg-background-surface border-border-default inline-flex flex-col items-start justify-start gap-3 self-stretch rounded-xl border p-4'>
                <EmailInput
                    id='basic-auth-email'
                    required
                    label={CommonMessage.FORMS.EMAIL()}
                    value={formValues.email}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, email: value }))}
                    isValidCallback={setEmailValid}
                />
                <PasswordInput
                    id='basic-auth-old-password'
                    required
                    label={
                        <FormattedMessage
                            id='new-password-page.old-password'
                            description='Label for old password input on the new password page'
                            defaultMessage='Old password'
                        />
                    }
                    value={formValues.old_password}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, old_password: value }))}
                    isValidCallback={setOldPasswordValid}
                />
                <PasswordInput
                    id='basic-auth-new-password'
                    required
                    strength
                    noAutoComplete
                    label={
                        <FormattedMessage
                            id='new-password-page.new-password'
                            description='Label for new password input on the new password page'
                            defaultMessage='New password'
                        />
                    }
                    value={formValues.new_password}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, new_password: value }))}
                    isValidCallback={setNewPasswordValid}
                />
                <PasswordInput
                    id='basic-auth-confirm-new-password'
                    required
                    noAutoComplete
                    label={
                        <FormattedMessage
                            id='basic-auth-update.repeat-new-password'
                            description='Label for repeating the new password in basic auth update'
                            defaultMessage='Repeat password'
                        />
                    }
                    value={formValues.confirm_new_password}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, confirm_new_password: value }))}
                    isValidCallback={setConfirmNewPasswordValid}
                />
                {!passwordsMatch && formValues.confirm_new_password && (
                    <div className='text-error text-sm'>
                        <FormattedMessage
                            id='basic-auth-update.passwords-must-match'
                            description='Validation message when repeated basic auth password does not match'
                            defaultMessage='Passwords must match.'
                        />
                    </div>
                )}
            </div>

            <div className='flex w-full items-start justify-end gap-3'>
                <DefaultPrimaryButton
                    text={CommonMessage.BUTTONS.SUBMIT}
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    isLoading={isSubmitting}
                />
            </div>
        </form>
    );
};
